import React, { useMemo, useState } from 'react';

import { Alert, Button, Form, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';

import { bandTier } from 'components/upb_match_explorer/constants';
import {
  BG_1,
  BG_2,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  PRIMARY_COLOR,
  RADIUS_LG,
  SHADOW_SM,
} from 'components/upb_viewer_beta/css/betaTokens';
import { fmtInt, fmtPct, fmtUsd } from 'components/upb_match_explorer/format';
import FsaLoader from 'components/shared/FsaLoader';
import useGet from 'lib/hooks/useGet';
import { MANUFACTURER_SKU_MAPPING_INSTACART_DASHBOARD } from 'lib/networking/endpoints';
import InfluenceStatCard from 'components/influence_sku_mapping/InfluenceStatCard';

/**
 * InstacartDashboard — the "Instacart" tab on the Influence SKU mapping page.
 *
 * Distributor-centric view: one row per Instacart-enabled distributor with
 * TAM/mapped SKU counts, GMV, and mapping rate. The campaign-brand scope comes
 * from Instacart-campaign-enabled manufacturer products.
 */
function InstacartDashboard() {
  const { data, loading, error, refetch } = useGet(MANUFACTURER_SKU_MAPPING_INSTACART_DASHBOARD);
  const [query, setQuery] = useState('');

  const distributors = useMemo(() => data?.distributors || [], [data]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return distributors;
    return distributors.filter(row => (row.name || '').toLowerCase().includes(needle));
  }, [distributors, query]);

  if (loading && !data) {
    return (
      <div className={css(styles.loadingWrap)}>
        <FsaLoader fullView size={96} label="Loading Instacart dashboard…" />
      </div>
    );
  }

  const totals = data?.totals || {};

  return (
    <div>
      <div className={css(styles.overviewTag)}>OVERVIEW</div>
      <h1 className={css(styles.title)}>Instacart readiness by distributor</h1>
      <p className={css(styles.subtitle)}>
        Mapped SKUs as a share of TAM for Instacart-enabled distributors. Search by name to filter
        the list.
      </p>

      {error && (
        <Alert variant="danger" className={css(styles.errorBanner)}>
          <span>Failed to load the Instacart dashboard.</span>
          <Button variant="outline-danger" size="sm" onClick={refetch}>
            Retry
          </Button>
        </Alert>
      )}

      <div className={css(styles.cardGrid)}>
        <InfluenceStatCard
          accent="primary"
          label="Campaign MPNs"
          value={fmtInt(totals.campaign_skus)}
          caption="Instacart campaign-enrolled catalog SKUs"
          ratio={
            totals.active_catalog_skus
              ? (totals.campaign_skus / totals.active_catalog_skus) * 100
              : 0
          }
          footer={`of ${fmtInt(totals.active_catalog_skus)} active on C&D`}
        />
        <InfluenceStatCard
          accent="success"
          label="Mapped SKUs"
          value={fmtPct(totals.mapped_pct)}
          caption="DP SKUs mapped to the mfr catalog · of TAM"
          ratio={totals.mapped_pct}
          footer={`${fmtInt(totals.mapped)} / ${fmtInt(totals.tam)} SKUs`}
        />
        <InfluenceStatCard
          accent="info"
          label="Mapped GMV"
          value={fmtPct(totals.gmv_pct)}
          caption="DP GMV mapped to the mfr catalog · of TAM GMV"
          ratio={totals.gmv_pct}
          footer={`${fmtUsd(totals.mapped_gmv)} / ${fmtUsd(totals.tam_gmv)}`}
        />
        <InfluenceStatCard
          accent="warning"
          label="Instacart DPs"
          value={`${fmtInt(totals.dp_count)} / ${fmtInt(totals.total_instacart_dps)}`}
          caption="Distributors with TAM SKUs · of all Instacart DPs"
          ratio={
            totals.total_instacart_dps ? (totals.dp_count / totals.total_instacart_dps) * 100 : 0
          }
        />
      </div>

      <div className={css(styles.listHead)}>
        <div>
          <h2 className={css(styles.listTitle)}>Instacart distributor list</h2>
          <div className={css(styles.listMeta)}>
            {`Showing ${filtered.length} of ${distributors.length} — highest GMV first`}
          </div>
        </div>
        <Form.Control
          className={css(styles.search)}
          type="search"
          placeholder="Filter distributors"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      <div className={css(styles.tableRegion)}>
        <div className={css(loading && styles.tableDimmed)}>
          <div className={css(styles.tableWrap)}>
            <Table hover className={css(styles.table)}>
              <thead>
                <tr>
                  <th>Distributor</th>
                  <th className={css(styles.numCol)}>Mapped / TAM</th>
                  <th className={css(styles.numCol)}>Mapped %</th>
                  <th className={css(styles.numCol)}>GMV %</th>
                  <th className={css(styles.numCol)}>TAM GMV</th>
                  <th className={css(styles.numCol)}>Mapped GMV</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="6" className={css(styles.empty)}>
                      {error
                        ? 'Could not load distributor data.'
                        : distributors.length === 0
                        ? 'No Instacart campaign data available yet.'
                        : 'No distributors match the current search.'}
                    </td>
                  </tr>
                ) : (
                  filtered.map(row => {
                    const tier = bandTier(row.band);
                    return (
                      <tr key={row.distributor_id}>
                        <td>
                          <span className={css(styles.dpName)}>{row.name || '—'}</span>
                        </td>
                        <td className={css(styles.numCol)}>
                          <span className={css(styles.mappedFrac)}>{fmtInt(row.mapped)}</span>
                          <span className={css(styles.tamDenom)}> / {fmtInt(row.tam)}</span>
                        </td>
                        <td className={css(styles.numCol)}>{fmtPct(row.mapped_pct)}</td>
                        <td className={css(styles.numCol)}>
                          <span
                            className={css(styles.pctPill)}
                            style={{
                              backgroundColor: tier.bg,
                              color: tier.text,
                            }}
                          >
                            {fmtPct(row.gmv_pct)}
                          </span>
                        </td>
                        <td className={css(styles.numCol)}>{fmtUsd(row.tam_gmv)}</td>
                        <td className={css(styles.numCol)}>{fmtUsd(row.mapped_gmv)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>
        {loading && (
          <div className={css(styles.tableSpinner)}>
            <FsaLoader size={48} />
          </div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  loadingWrap: { background: BG_2 },
  overviewTag: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    color: PRIMARY_COLOR,
  },
  title: { fontSize: 26, fontWeight: 700, color: FG_1, margin: '6px 0 6px' },
  subtitle: { fontSize: 14, color: FG_2, maxWidth: 760, marginBottom: 22 },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 16,
    marginBottom: 8,
    '@media (max-width: 1200px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  listHead: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    margin: '30px 0 14px',
  },
  listTitle: { fontSize: 18, fontWeight: 700, color: FG_1, marginBottom: 4 },
  listMeta: { fontSize: 13, color: FG_3 },
  search: {
    width: 240,
    height: 38,
    fontSize: 13,
    borderRadius: RADIUS_LG,
    border: `1px solid ${BORDER_2}`,
    background: BG_1,
  },
  tableRegion: { position: 'relative' },
  tableDimmed: {
    opacity: 0.45,
    pointerEvents: 'none',
    transition: 'opacity 120ms ease',
  },
  tableSpinner: {
    position: 'absolute',
    top: 48,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    zIndex: 2,
  },
  tableWrap: {
    background: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_SM,
    overflowX: 'auto',
  },
  table: { fontSize: 13, color: FG_2, marginBottom: 0 },
  numCol: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  dpName: { fontWeight: 600, color: FG_1 },
  mappedFrac: { fontWeight: 600, color: FG_1 },
  tamDenom: { color: FG_3 },
  pctPill: {
    display: 'inline-block',
    minWidth: 58,
    textAlign: 'center',
    fontWeight: 600,
    fontSize: 12.5,
    padding: '3px 8px',
    borderRadius: 20,
  },
  empty: { textAlign: 'center', color: FG_3, padding: '28px 8px' },
});

export default InstacartDashboard;
